import { useCallback, useEffect, useRef } from 'react';

/**
 * Accessible image lightbox.
 *
 * Props:
 *  - images: [{ url, alt_text }]  (already ordered)
 *  - index:  currently shown image index (>= 0 to open, null/-1 to close)
 *  - onClose(): dismiss
 *  - onIndexChange(nextIndex): move to another image
 *
 * Behavior:
 *  - Escape closes; ArrowLeft / ArrowRight navigate (wrap-around).
 *  - Focus moves into the dialog on open and is restored to the trigger on close.
 *  - Focus is trapped inside the dialog (Tab cycles through its controls).
 *  - Body scroll is locked while open.
 *  - Motion (fade/scale) is neutralized globally under prefers-reduced-motion
 *    via the reduce block in index.css, so no JS gating is required.
 */
export default function Lightbox({ images = [], index, onClose, onIndexChange }) {
  const open = index != null && index >= 0 && index < images.length;
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const restoreFocusRef = useRef(null);

  const go = useCallback(
    (delta) => {
      if (!images.length) return;
      const next = (index + delta + images.length) % images.length;
      onIndexChange?.(next);
    },
    [images.length, index, onIndexChange]
  );

  // Keyboard handling + focus trap while open.
  useEffect(() => {
    if (!open) return undefined;

    restoreFocusRef.current = document.activeElement;
    // Move focus into the dialog after paint.
    const focusTimer = window.setTimeout(() => closeRef.current?.focus(), 0);

    function onKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        go(1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        go(-1);
      } else if (e.key === 'Tab') {
        const focusable = dialogRef.current?.querySelectorAll(
          'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      // Restore focus to whatever opened the lightbox.
      if (restoreFocusRef.current instanceof HTMLElement) {
        restoreFocusRef.current.focus();
      }
    };
  }, [open, go, onClose]);

  if (!open) return null;

  const current = images[index];
  const multiple = images.length > 1;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-maroon-950/90 p-4 animate-fade-in-slow sm:p-8"
      onClick={onClose}
    >
      <button
        ref={closeRef}
        type="button"
        onClick={onClose}
        aria-label="Close photo viewer"
        className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-2xl text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-400"
      >
        &times;
      </button>

      {multiple && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            go(-1);
          }}
          aria-label="Previous photo"
          className="absolute left-3 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-2xl text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-400 sm:left-6"
        >
          &#8249;
        </button>
      )}

      <figure
        className="flex max-h-full max-w-5xl animate-scale-in flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={current.url}
          alt={current.alt_text || ''}
          className="max-h-[80vh] w-auto max-w-full rounded-lg object-contain shadow-lift"
        />
        <figcaption className="mt-3 flex items-center gap-3 text-sm text-cream-200">
          {current.alt_text && <span>{current.alt_text}</span>}
          {multiple && (
            <span aria-live="polite" className="text-cream-300">
              {index + 1} / {images.length}
            </span>
          )}
        </figcaption>
      </figure>

      {multiple && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            go(1);
          }}
          aria-label="Next photo"
          className="absolute right-3 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-2xl text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-400 sm:right-6"
        >
          &#8250;
        </button>
      )}
    </div>
  );
}
